import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { getSopLibrary } from "../lib/api";
import {
    BookOpen,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Loader2,
    Search,
    Filter,
    FileText,
    AlertTriangle,
    HardDrive,
    Network,
    Zap,
    GitCompareArrows,
} from "lucide-react";
import { toast } from "sonner";
import { bulkReauditSOP } from "../lib/api";
import { useAuth } from "../lib/auth";
import SopDiffModal from "../components/SopDiffModal";

const DOC_TYPES = [
    ["SOP", "SOPs"],
    ["URS", "URS"],
    ["Validation Report", "Validation Reports"],
    ["Change Control", "Change Controls"],
    ["Deviation", "Deviations"],
];

const SourceChip = ({ source, label }) => {
    // AudRI Object Storage is our fallback. Highlight DMS-backed docs as the
    // preferred enterprise pattern ("documents-in-place").
    const isAudri = source === "audri_storage";
    return (
        <span
            className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm ${
                isAudri
                    ? "border-hair text-neutral-600 bg-white"
                    : "border-[#064E3B]/30 text-[#064E3B] bg-[#ECFDF5]"
            }`}
            title={
                isAudri
                    ? "Stored in AudRI Object Storage (bit-for-bit, 25MB limit)"
                    : `Connected in-place from ${label}`
            }
            data-testid={`src-${source}`}
        >
            {label}
        </span>
    );
};

const Stat = ({ label, value, sub, testid, accent }) => (
    <div
        className="p-8 border-r border-b border-hair bg-white"
        data-testid={testid}
    >
        <div className="overline mb-4">{label}</div>
        <div
            className={`heading text-4xl font-medium tracking-tighter leading-none ${
                accent ? "text-[#C2410C]" : ""
            }`}
        >
            {value}
        </div>
        {sub && <div className="text-xs text-neutral-500 mt-3 mono">{sub}</div>}
    </div>
);

export default function SopLibrary() {
    const [data, setData] = useState(null);
    const [docType, setDocType] = useState("SOP");
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(null); // filename currently expanded
    const [loading, setLoading] = useState(true);
    const [reauditing, setReauditing] = useState(null);
    const [diffFilename, setDiffFilename] = useState(null);
    const { user } = useAuth();
    const isReadOnly = user?.role === "Observer";

    const handleReaudit = async (filename, systemCount) => {
        if (reauditing) return;
        const ok = window.confirm(
            `Re-audit ${systemCount} system${
                systemCount === 1 ? "" : "s"
            } citing ${filename}?\n\n` +
                `This queues a periodic review on each — uses LLM credits ` +
                `(roughly $${(0.1 * systemCount).toFixed(2)} total).`,
        );
        if (!ok) return;
        setReauditing(filename);
        const t = toast.loading(
            `Queuing audits across ${systemCount} system${
                systemCount === 1 ? "" : "s"
            }…`,
        );
        try {
            const res = await bulkReauditSOP(filename);
            toast.success(
                `${res.system_count} audit${
                    res.system_count === 1 ? "" : "s"
                } running. Open each system to watch progress.`,
                { id: t, duration: 7000 },
            );
        } catch (e) {
            toast.error(
                e?.response?.status === 403
                    ? "Read-only demo — bulk re-audit is disabled. Contact sales for a pilot."
                    : e?.response?.data?.detail ||
                      "Couldn't queue the bulk audit.",
                { id: t },
            );
        } finally {
            setReauditing(null);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getSopLibrary(docType)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [docType]);

    const filtered = useMemo(() => {
        if (!data?.sops) return [];
        const q = query.trim().toLowerCase();
        if (!q) return data.sops;
        return data.sops.filter(
            (s) =>
                s.filename.toLowerCase().includes(q) ||
                s.systems.some((x) => x.name.toLowerCase().includes(q)),
        );
    }, [data, query]);

    const totals = data?.totals || {};

    return (
        <AppLayout>
            <PageHeader
                eyebrow="Workspace · Compliance"
                title="SOP Library"
                sub="Every governing document across your GxP estate — one row per SOP, joined to every system that cites it. Flip the mental model from 'system → documents' to 'document → systems'."
                right={
                    <div className="flex gap-3 flex-wrap items-center">
                        <div className="relative">
                            <Search
                                size={13}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                            />
                            <input
                                data-testid="sop-search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter by filename or system…"
                                className="text-sm border border-hair pl-8 pr-3 py-2 rounded-sm bg-white w-64 focus:outline-none focus:border-[#C2410C]"
                            />
                        </div>
                        <div className="inline-flex items-center border border-hair rounded-sm bg-white text-sm overflow-hidden">
                            <span className="px-3 py-2 overline border-r border-hair flex items-center gap-1 text-neutral-500">
                                <Filter size={12} /> TYPE
                            </span>
                            {DOC_TYPES.map(([v, l]) => (
                                <button
                                    key={v}
                                    data-testid={`sop-type-${v}`}
                                    onClick={() => setDocType(v)}
                                    className={`px-3 py-2 border-r border-hair last:border-r-0 ${
                                        docType === v
                                            ? "bg-[#0a0a0a] text-white"
                                            : "hover:bg-neutral-50"
                                    }`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>
                }
            />

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-hair">
                <Stat
                    label="Unique SOPs"
                    value={totals.unique_sops ?? "—"}
                    sub="Deduped across systems"
                    testid="sop-kpi-unique"
                />
                <Stat
                    label="Systems covered"
                    value={totals.systems_covered ?? "—"}
                    sub="GxP systems citing an SOP"
                    testid="sop-kpi-systems"
                />
                <Stat
                    label="Avg systems / SOP"
                    value={totals.avg_systems_per_sop ?? 0}
                    sub="Leverage ratio"
                    testid="sop-kpi-leverage"
                    accent
                />
                <Stat
                    label="Critical gaps"
                    value={totals.critical_gaps ?? 0}
                    sub="Across systems citing SOPs"
                    testid="sop-kpi-gaps"
                />
            </div>

            {/* Documents-in-place pitch */}
            <div className="px-8 md:px-12 pt-8">
                <div
                    data-testid="dms-pitch"
                    className="border border-hair bg-white p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start"
                >
                    <div className="w-10 h-10 border border-hair rounded-sm flex items-center justify-center shrink-0">
                        <Network size={18} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="overline mb-2">
                            Documents-in-place · zero migration
                        </div>
                        <h3 className="heading text-lg md:text-xl font-medium tracking-tight">
                            AudRI reads from where your documents already live.
                        </h3>
                        <p className="text-sm text-neutral-600 mt-3 leading-relaxed max-w-2xl">
                            If you run Documentum, Veeva Vault, SharePoint or
                            TrackWise Digital — we connect in-place. Your
                            controlled docs stay in your validated DMS. They keep
                            their revision control, approval workflow and audit
                            trail; AudRI only reads the latest approved revision
                            and extracts the rules.
                        </p>
                        <p className="text-sm text-neutral-600 mt-3 leading-relaxed max-w-2xl">
                            No DMS yet? The{" "}
                            <span className="mono text-[11px] uppercase tracking-wider text-[#0a0a0a] bg-[#f5f5f4] px-2 py-[2px] border border-hair rounded-sm">
                                AudRI Object Storage
                            </span>{" "}
                            source of record is our fallback — bit-for-bit file
                            retention, SHA-256 integrity, 21 CFR Part 11–ready.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-2 text-xs md:min-w-[220px]">
                        {[
                            ["Veeva Vault", "LIVE", "#064E3B"],
                            ["SharePoint", "LIVE", "#064E3B"],
                            ["TrackWise Digital", "LIVE", "#064E3B"],
                            ["Documentum", "OFF", "#737373"],
                        ].map(([name, status, color]) => (
                            <div
                                key={name}
                                className="flex items-center gap-2 border border-hair px-3 py-[6px] rounded-sm bg-white"
                            >
                                <span
                                    className="w-[6px] h-[6px] rounded-full"
                                    style={{ backgroundColor: color }}
                                />
                                <span className="text-sm flex-1 truncate">{name}</span>
                                <span
                                    className="mono text-[10px] uppercase tracking-wider"
                                    style={{ color }}
                                >
                                    {status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* SOP table */}
            <div className="p-8 md:p-12">
                {loading && (
                    <div className="py-16 text-center text-neutral-500">
                        <Loader2 className="animate-spin inline" size={18} />{" "}
                        Loading library…
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div
                        data-testid="sop-empty"
                        className="border border-dashed border-hair bg-white p-16 text-center"
                    >
                        <BookOpen
                            className="mx-auto text-neutral-300"
                            size={32}
                        />
                        <p className="text-sm text-neutral-500 mt-4">
                            {query
                                ? "No matches for your filter."
                                : "No documents yet. Upload SOPs on any system — they'll appear here grouped across systems."}
                        </p>
                    </div>
                )}

                {!loading && filtered.length > 0 && (
                    <div
                        className="border border-hair bg-white"
                        data-testid="sop-table"
                    >
                        <div className="grid grid-cols-[24px_minmax(0,3fr)_90px_90px_1fr_90px_140px_120px] px-4 py-3 border-b border-hair overline text-[10px] gap-4">
                            <div />
                            <div>Filename</div>
                            <div className="text-center">Rev</div>
                            <div className="text-center">Systems</div>
                            <div>Source of record</div>
                            <div className="text-center">Rules</div>
                            <div className="text-center">Critical findings</div>
                            <div className="text-right">Last updated</div>
                        </div>

                        {filtered.map((s) => {
                            const isOpen = expanded === s.filename;
                            return (
                                <div
                                    key={s.filename}
                                    className="border-b border-hair last:border-b-0"
                                    data-testid={`sop-row-${s.filename}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setExpanded(isOpen ? null : s.filename)
                                        }
                                        className={`w-full grid grid-cols-[24px_minmax(0,3fr)_90px_90px_1fr_90px_140px_120px] px-4 py-3 items-center text-left gap-4 hover:bg-neutral-50 ${
                                            isOpen ? "bg-neutral-50" : ""
                                        }`}
                                    >
                                        <span className="text-neutral-400">
                                            {isOpen ? (
                                                <ChevronDown size={14} />
                                            ) : (
                                                <ChevronRight size={14} />
                                            )}
                                        </span>
                                        <span className="flex items-center gap-2 min-w-0">
                                            <FileText
                                                size={13}
                                                className="text-neutral-400 shrink-0"
                                            />
                                            <span className="text-sm truncate">
                                                {s.filename}
                                            </span>
                                            <span className="mono text-[10px] text-neutral-400 shrink-0">
                                                {s.doc_type}
                                            </span>
                                        </span>
                                        <span className="mono text-xs text-center text-neutral-600">
                                            v{s.latest_revision}
                                        </span>
                                        <span className="num text-sm text-center">
                                            {s.system_count}
                                        </span>
                                        <span className="flex flex-wrap gap-1">
                                            {s.storage_sources.map((src, i) => (
                                                <SourceChip
                                                    key={src}
                                                    source={src}
                                                    label={
                                                        s.storage_sources_labels[
                                                            i
                                                        ] || src
                                                    }
                                                />
                                            ))}
                                        </span>
                                        <span className="num text-xs text-center text-neutral-600">
                                            {s.rules_extracted}
                                        </span>
                                        <span
                                            className={`num text-sm text-center ${
                                                s.critical_findings > 0
                                                    ? "text-[#991B1B] font-medium"
                                                    : "text-neutral-400"
                                            }`}
                                        >
                                            {s.critical_findings}
                                            {s.critical_findings > 0 && (
                                                <AlertTriangle
                                                    size={11}
                                                    className="inline ml-1 mb-[2px]"
                                                />
                                            )}
                                        </span>
                                        <span className="mono text-[11px] text-right text-neutral-500">
                                            {s.latest_uploaded_at
                                                ? new Date(
                                                      s.latest_uploaded_at,
                                                  ).toLocaleDateString()
                                                : "—"}
                                        </span>
                                    </button>

                                    {isOpen && (
                                        <div
                                            className="px-8 py-6 border-t border-hair bg-[#fafafa]"
                                            data-testid={`sop-expanded-${s.filename}`}
                                        >
                                            {/* Bulk actions strip */}
                                            <div className="flex flex-wrap gap-2 mb-6 pb-5 border-b border-hair">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleReaudit(
                                                            s.filename,
                                                            s.system_count,
                                                        )
                                                    }
                                                    disabled={
                                                        reauditing === s.filename ||
                                                        s.system_count === 0 ||
                                                        isReadOnly
                                                    }
                                                    data-testid={`btn-reaudit-${s.filename}`}
                                                    title={
                                                        isReadOnly
                                                            ? "Read-only demo — disabled"
                                                            : `Re-audit every system citing this ${s.doc_type}`
                                                    }
                                                    className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-xs px-3 py-2 rounded-sm hover:bg-[#9A3412] disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {reauditing === s.filename ? (
                                                        <Loader2
                                                            size={12}
                                                            className="animate-spin"
                                                        />
                                                    ) : (
                                                        <Zap size={12} />
                                                    )}
                                                    Re-audit {s.system_count} system
                                                    {s.system_count === 1
                                                        ? ""
                                                        : "s"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setDiffFilename(
                                                            s.filename,
                                                        )
                                                    }
                                                    disabled={
                                                        s.revisions_across_systems <
                                                        2
                                                    }
                                                    data-testid={`btn-diff-${s.filename}`}
                                                    title={
                                                        s.revisions_across_systems <
                                                        2
                                                            ? "Only one revision present — nothing to compare"
                                                            : "Compare the two most-distinct revisions"
                                                    }
                                                    className="inline-flex items-center gap-2 border border-hair bg-white text-xs px-3 py-2 rounded-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <GitCompareArrows size={12} />
                                                    Compare revisions
                                                </button>
                                                <span className="ml-auto self-center mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                    {s.revisions_across_systems}{" "}
                                                    distinct content rev
                                                    {s.revisions_across_systems ===
                                                    1
                                                        ? ""
                                                        : "s"}
                                                </span>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-8">
                                                <div>
                                                    <div className="overline mb-3">
                                                        Systems citing this{" "}
                                                        {s.doc_type}
                                                    </div>
                                                    <div className="space-y-2">
                                                        {s.systems.map((sys) => (
                                                            <Link
                                                                key={sys.id}
                                                                to={`/systems/${sys.id}`}
                                                                data-testid={`sop-sys-link-${sys.name}`}
                                                                className="flex items-center justify-between px-4 py-3 border border-hair bg-white rounded-sm hover:border-[#C2410C] group"
                                                            >
                                                                <span className="flex items-center gap-3">
                                                                    <span className="w-[6px] h-[6px] rounded-full bg-[#C2410C]" />
                                                                    <span className="heading font-medium tracking-tight text-sm">
                                                                        {sys.name}
                                                                    </span>
                                                                </span>
                                                                <ExternalLink
                                                                    size={12}
                                                                    className="text-neutral-400 group-hover:text-[#C2410C]"
                                                                />
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="overline mb-3">
                                                        Provenance
                                                    </div>
                                                    <dl className="text-sm space-y-2">
                                                        <div className="flex justify-between">
                                                            <dt className="text-neutral-500">
                                                                Latest revision
                                                            </dt>
                                                            <dd className="mono">
                                                                v{s.latest_revision}
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <dt className="text-neutral-500">
                                                                Distinct content hashes
                                                            </dt>
                                                            <dd className="mono">
                                                                {
                                                                    s.revisions_across_systems
                                                                }
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <dt className="text-neutral-500">
                                                                Source of record
                                                            </dt>
                                                            <dd className="flex gap-1 flex-wrap justify-end">
                                                                {s.storage_sources.map(
                                                                    (src, i) => (
                                                                        <SourceChip
                                                                            key={src}
                                                                            source={
                                                                                src
                                                                            }
                                                                            label={
                                                                                s
                                                                                    .storage_sources_labels[
                                                                                    i
                                                                                ] ||
                                                                                src
                                                                            }
                                                                        />
                                                                    ),
                                                                )}
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <dt className="text-neutral-500">
                                                                Total size (all revs)
                                                            </dt>
                                                            <dd className="mono">
                                                                {(
                                                                    s.total_size /
                                                                    1024
                                                                ).toFixed(1)}{" "}
                                                                KB
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <dt className="text-neutral-500">
                                                                Original retained
                                                            </dt>
                                                            <dd className="mono">
                                                                {s.has_file ? (
                                                                    <span className="text-[#064E3B]">
                                                                        <HardDrive
                                                                            size={
                                                                                11
                                                                            }
                                                                            className="inline mr-1 mb-[2px]"
                                                                        />
                                                                        yes
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-neutral-500">
                                                                        text-only
                                                                    </span>
                                                                )}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {diffFilename && (
                <SopDiffModal
                    filename={diffFilename}
                    onClose={() => setDiffFilename(null)}
                />
            )}
        </AppLayout>
    );
}
